import React, { useEffect, useState, useRef } from 'react';
import { Plus, Edit2, Trash2, X, AlertCircle, Search, Truck, ArrowRight } from 'lucide-react';
import { getPenugasan, createPenugasan, updatePenugasan, deletePenugasan, getKaryawan, getVehicles, getPenugasanRollover, getPenugasanCopyPrevious } from '../api';
import { useAuth } from '../AuthContext';

// ====== Searchable Vehicle Selector ======
const SEARCH_MODES = [
  { key: 'body_no', label: 'No Body' },
  { key: 'plate_no', label: 'No Polisi' },
];

function VehicleSearchInput({ vehicles, onSelect }) {
  const [query, setQuery] = useState('');
  const [searchMode, setSearchMode] = useState('body_no');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = query
    ? vehicles.filter(v => {
        const val = (v[searchMode] || '').toLowerCase();
        return val.includes(query.toLowerCase());
      })
    : vehicles;

  const modeLabel = SEARCH_MODES.find(m => m.key === searchMode)?.label || 'No Body';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4,
        border: '1px solid var(--gray-200)', borderRadius: 8,
        background: '#fff',
      }}>
        <select value={searchMode} onChange={e => setSearchMode(e.target.value)}
          style={{
            border: 'none', outline: 'none', background: 'transparent',
            padding: '9px 4px 9px 10px', fontSize: '0.78rem',
            color: 'var(--gray-500)', cursor: 'pointer', flexShrink: 0,
            borderRight: '1px solid var(--gray-100)',
          }}>
          {SEARCH_MODES.map(m => (
            <option key={m.key} value={m.key}>{m.label}</option>
          ))}
        </select>
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={`Cari ${modeLabel}...`}
          style={{
            flex: 1, border: 'none', outline: 'none', padding: '9px 6px 9px 4px',
            fontSize: '0.82rem', borderRadius: 8, background: 'transparent',
          }}
        />
        {query && (
          <X size={14} style={{ marginRight: 8, cursor: 'pointer', color: 'var(--gray-400)' }}
            onClick={() => { setQuery(''); setOpen(true); }} />
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)', maxHeight: 240, overflow: 'auto',
          marginTop: 4,
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '14px', textAlign: 'center', color: 'var(--gray-400)', fontSize: '0.82rem' }}>
              {query ? 'Kendaraan tidak ditemukan' : 'Semua kendaraan sudah dipilih'}
            </div>
          ) : (
            filtered.map(v => (
              <div key={v.id}
                onClick={() => { onSelect(v.id); setQuery(''); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '9px 12px', cursor: 'pointer', fontSize: '0.82rem',
                  borderBottom: '1px solid var(--gray-50)',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                <Truck size={14} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong>{v.body_no || '-'}</strong>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                    {v.plate_no || ''}{v.trayek_no ? `, ${v.trayek_no}` : ''}
                  </div>
                </div>
                {v._assignedTo ? (
                  <span style={{ fontSize: '0.7rem', color: '#f5365c', background: '#ffe8eb', padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>
                    {v._assignedTo}
                  </span>
                ) : (
                  <Plus size={14} style={{ color: '#2dce89', flexShrink: 0 }} />
                )}
              </div>
            ))
          )}
          {filtered.length > 0 && (
            <div style={{ padding: '6px 12px', fontSize: '0.7rem', color: 'var(--gray-400)', borderTop: '1px solid var(--gray-100)' }}>
              {filtered.length} kendaraan{query ? ` (filter: "${query}")` : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const emptyForm = {
  karyawan_id: '', lokasi: '', tanggal_mulai: '', tanggal_selesai: '', deskripsi: '', vehicle_data: [],
};

// Helper: rentang tanggal awal & akhir bulan berjalan (YYYY-MM-DD, dihitung lokal agar tidak kena bug timezone UTC)
const getCurrentMonthRange = () => {
  const now = new Date();
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return {
    awal: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
    akhir: fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

// ====== Tampilan Tabel Penugasan per Teknisi ======
const PAGE_SIZE = 10;

// Nomor halaman untuk tombol pagination (dengan ellipsis jika banyak halaman)
const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...set].filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  sorted.forEach(p => {
    if (p - prev > 1) out.push('...');
    out.push(p);
    prev = p;
  });
  return out;
};

// Palet warna soft untuk header tabel tiap teknisi (agar mudah dibedakan)
const TEKNISI_COLORS = [
  { bg: '#e3f2fd', border: '#bbdefb', text: '#0d47a1' }, // biru
  { bg: '#e8f5e9', border: '#c8e6c9', text: '#1b5e20' }, // hijau
  { bg: '#fff3e0', border: '#ffe0b2', text: '#e65100' }, // oranye
  { bg: '#f3e5f5', border: '#e1bee7', text: '#4a148c' }, // ungu
  { bg: '#e0f7fa', border: '#b2ebf2', text: '#006064' }, // cyan
  { bg: '#fce4ec', border: '#f8bbd0', text: '#880e4f' }, // pink
  { bg: '#efebe9', border: '#d7ccc8', text: '#3e2723' }, // coklat
  { bg: '#e8eaf6', border: '#c5cae9', text: '#1a237e' }, // indigo
];

// Satu teknisi = satu tabel. Baris = kendaraan yang ditugaskan. Pagination 10 baris/halaman.
function PenugasanTable({ items, rows, teknisiName, periode, keterangan, color, canEdit, canDelete, onEdit, onDelete, filterNoBody }) {
  const [page, setPage] = useState(1);
  const [showManage, setShowManage] = useState(false);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  // Clamp defensif: pastikan halaman aktif tidak pernah melebihi jumlah halaman
  const safePage = Math.min(page, pageCount);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const pageRows = rows.slice(startIdx, startIdx + PAGE_SIZE);

  // Kembali ke halaman 1 jika jumlah data berubah (mis. karena filter berubah)
  useEffect(() => { setPage(1); }, [rows.length]);

  const th = {
    padding: '10px 12px', textAlign: 'left', fontSize: '0.68rem', fontWeight: 600,
    color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: '1px solid var(--gray-100)', whiteSpace: 'nowrap', background: 'var(--gray-50)',
  };
  const td = {
    padding: '10px 12px', borderBottom: '1px solid var(--gray-100)',
    fontSize: '0.8rem', color: 'var(--gray-500)', verticalAlign: 'top',
  };

  return (
    <div style={{
      background: '#fff', borderRadius: 10, border: '1px solid var(--gray-200)',
      overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    }}>
      {/* Header teknisi dengan warna soft */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
        background: color.bg, borderBottom: `1px solid ${color.border}`,
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: '50%', background: color.text, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: '0.85rem', flexShrink: 0,
        }}>
          {(teknisiName || '?').charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: color.text, fontSize: '0.92rem' }}>{teknisiName}</span>
            {filterNoBody.trim() && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 10px', borderRadius: 20,
                fontSize: '0.72rem', fontWeight: 600,
                background: '#fff3cd', color: '#856404',
                border: '1px solid #ffeeba', whiteSpace: 'nowrap',
              }}>
                No Body: {filterNoBody.trim()}
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.72rem', color: color.text, opacity: 0.78 }}>
            Periode: {periode} · {rows.length} kendaraan
          </div>
        </div>
        {canEdit || canDelete ? (
          <button className="btn btn-secondary" onClick={() => setShowManage(v => !v)}
            style={{ padding: '4px 10px', fontSize: '0.72rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {showManage ? 'Tutup' : 'Kelola'} ({items.length})
          </button>
        ) : (
          <Truck size={16} style={{ color: color.text, opacity: 0.5, flexShrink: 0 }} />
        )}
      </div>

      {/* Panel kelola penugasan teknisi ini (edit/hapus tetap tersedia) */}
      {showManage && (
        <div style={{ background: '#fcfcfc', borderBottom: '1px solid var(--gray-100)', padding: '6px 16px' }}>
          {items.length === 0 ? (
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', padding: '4px 0' }}>Tidak ada penugasan</div>
          ) : (
            items.map(d => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--gray-50)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.tanggal_mulai?.slice(0, 10)} {d.tanggal_selesai ? `s/d ${d.tanggal_selesai?.slice(0, 10)}` : ''} — {(d.vehicle_data || []).length} kendaraan{d.status ? ` · ${d.status}` : ''}
                </span>
                {canEdit && <button className="btn-icon" title="Edit penugasan" onClick={() => onEdit(d)}><Edit2 size={13} /></button>}
                {canDelete && <button className="btn-icon btn-danger" title="Hapus penugasan" onClick={() => onDelete(d)}><Trash2 size={13} /></button>}
              </div>
            ))
          )}
        </div>
      )}

      {/* Keterangan (deskripsi penugasan) */}
      {keterangan.length > 0 && (
        <div style={{ padding: '8px 16px', fontSize: '0.75rem', color: 'var(--gray-500)', background: '#fafafa', borderBottom: '1px solid var(--gray-100)' }}>
          <strong style={{ color: 'var(--gray-600)' }}>Keterangan:</strong> {keterangan}
        </div>
      )}

      {/* Tabel data */}
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table" style={{ minWidth: 780 }}>
          <thead>
            <tr>
              <th style={th}>No</th>
              <th style={th}>No Body</th>
              <th style={th}>No Polisi</th>
              <th style={th}>Rute</th>
              <th style={th}>Lokasi (Pengendapan)</th>
              <th style={th}>AC/Non AC</th>
              <th style={th}>Client</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((v, idx) => {
              const isMatch = filterNoBody.trim() && (v.body_no || '').toLowerCase().includes(filterNoBody.trim().toLowerCase());
              return (
                <tr key={v.id != null ? v.id : `row-${idx}`}>
                  <td style={{ ...td, color: 'var(--gray-400)' }}>{startIdx + idx + 1}</td>
                  <td style={td}>
                    <strong style={{
                      color: isMatch ? '#856404' : 'var(--gray-700)',
                      background: isMatch ? '#fff3cd' : 'transparent',
                      padding: isMatch ? '2px 6px' : 0,
                      borderRadius: 4,
                    }}>
                      {v.body_no || '-'}
                    </strong>
                  </td>
                  <td style={td}>{v.plate_no || '-'}</td>
                  <td style={td}>{v.rute || '-'}</td>
                  <td style={td}>
                    {v.pengendapan ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#5e72e4', flexShrink: 0 }} />
                        {v.pengendapan}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={td}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 10px', borderRadius: 20,
                      fontSize: '0.72rem', fontWeight: 600,
                      background: v.jenis_ac === 'AC' ? '#e8f4fd' : '#f0f0f0',
                      color: v.jenis_ac === 'AC' ? '#0a66c2' : '#666',
                    }}>
                      {v.jenis_ac === 'AC' ? '❄ AC' : '🔧 Non AC'}
                    </span>
                  </td>
                  <td style={td}>{v.operator || '-'}</td>
                </tr>
              );
            })}
            {pageRows.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted">Belum ada kendaraan</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination di bawah tabel: jumlah halaman + tombol nomor halaman + navigasi */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
        padding: '8px 16px', borderTop: '1px solid var(--gray-100)', background: '#fafafa',
      }}>
        <span style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>
          Menampilkan {rows.length === 0 ? 0 : startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, rows.length)} dari {rows.length} kendaraan · Halaman {safePage} / {pageCount}
        </span>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <button className="btn btn-secondary" disabled={safePage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
            style={{ padding: '4px 10px', fontSize: '0.72rem' }}>‹</button>
          {getPageNumbers(safePage, pageCount).map((p, i) => (
            p === '...' ? (
              <span key={`dots-${i}`} style={{ fontSize: '0.72rem', color: 'var(--gray-400)', padding: '0 2px' }}>…</span>
            ) : (
              <button key={p} onClick={() => setPage(p)} disabled={p === safePage}
                style={{
                  minWidth: 26, height: 26, padding: '0 6px', borderRadius: 6, border: '1px solid var(--gray-200)',
                  fontSize: '0.72rem', cursor: 'pointer', fontWeight: p === safePage ? 700 : 500,
                  background: p === safePage ? 'var(--info, #11cdef)' : '#fff',
                  color: p === safePage ? '#fff' : 'var(--gray-600)',
                }}>
                {p}
              </button>
            )
          ))}
          <button className="btn btn-secondary" disabled={safePage >= pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))}
            style={{ padding: '4px 10px', fontSize: '0.72rem' }}>›</button>
        </div>
      </div>
    </div>
  );
}

export default function Penugasan() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [karyawanList, setKaryawanList] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterKaryawan, setFilterKaryawan] = useState('');
  const [filterNoBody, setFilterNoBody] = useState('');
  // Default filter tanggal: selalu bulan berjalan setiap kali menu dibuka
  const [filterTglAwal, setFilterTglAwal] = useState(getCurrentMonthRange().awal);
  const [filterTglAkhir, setFilterTglAkhir] = useState(getCurrentMonthRange().akhir);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [rolloverData, setRolloverData] = useState(null);
  const [copyPreviousData, setCopyPreviousData] = useState(null);
  const [copyLoading, setCopyLoading] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([getPenugasan(), getKaryawan(), getVehicles({ limit: 10000 })])
      .then(([res, karRes, vehRes]) => {
        setData(res.data);
        setKaryawanList(karRes.data);
        setVehicles(vehRes.data?.data || []);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Map kendaraan untuk lookup data terkini (dipakai filter No Body & pengelompokan)
  const vehicleById = new Map(vehicles.map(v => [v.id, v]));

  // Kata kunci No Body (dihitung sekali agar filter & tampilan konsisten)
  const noBodyQuery = filterNoBody.trim().toLowerCase();

  // Filter data
  const filtered = data.filter(d => {
    if (search) {
      const q = search.toLowerCase();
      if (!d.karyawan_nama?.toLowerCase().includes(q) &&
          !d.lokasi?.toLowerCase().includes(q)) return false;
    }
    if (filterKaryawan && d.karyawan_id !== Number(filterKaryawan)) return false;
    // Filter No Body: cocokkan no_body tersimpan di penugasan ATAU body_no terkini dari tabel kendaraan
    if (noBodyQuery) {
      const found = (d.vehicle_data || []).some(vd => {
        const veh = vd.id != null ? vehicleById.get(Number(vd.id)) : null;
        return (veh?.body_no || vd.no_body || '').toLowerCase().includes(noBodyQuery);
      });
      if (!found) return false;
    }
    // Filter tanggal S/D — diabaikan saat mencari No Body karena tujuannya lookup teknisi lintas periode
    if (!noBodyQuery) {
      const tglMulai = d.tanggal_mulai?.slice(0, 10) || '';
      if (filterTglAwal || filterTglAkhir) {
        if (!tglMulai) return false;
        if (filterTglAwal && tglMulai < filterTglAwal) return false;
        if (filterTglAkhir && tglMulai > filterTglAkhir) return false;
      }
    }
    return true;
  });

  // Kelompokkan kendaraan per teknisi untuk tampilan tabel (satu teknisi = satu tabel)
  const groupedByTeknisi = [...filtered.reduce((map, d) => {
    const key = d.karyawan_id || 0;
    if (!map.has(key)) {
      map.set(key, {
        karyawan_id: key,
        karyawan_nama: d.karyawan_nama || 'Tanpa Teknisi',
        items: [],
        vehicleIds: new Set(),
        rows: [],
        keterangan: new Set(),
        tglMulai: d.tanggal_mulai,
        tglSelesai: d.tanggal_selesai,
      });
    }
    const g = map.get(key);
    g.items.push(d);
    if (d.tanggal_mulai && (!g.tglMulai || d.tanggal_mulai < g.tglMulai)) g.tglMulai = d.tanggal_mulai;
    if (d.tanggal_selesai && (!g.tglSelesai || d.tanggal_selesai > g.tglSelesai)) g.tglSelesai = d.tanggal_selesai;
    if (d.deskripsi) g.keterangan.add(d.deskripsi.trim());
    (d.vehicle_data || []).forEach(vd => {
      const vid = vd.id;
      if (g.vehicleIds.has(vid)) return;
      g.vehicleIds.add(vid);
      const veh = vid != null ? vehicleById.get(Number(vid)) : null;
      g.rows.push({
        id: vid,
        body_no: veh?.body_no || vd.no_body || '',
        plate_no: veh?.plate_no || vd.no_polisi || '',
        rute: veh?.route_code || veh?.trayek_no || vd.trayek_no || '',
        pengendapan: veh?.pengendapan || vd.pengendapan || '',
        jenis_ac: veh?.jenis_ac || 'Non AC',
        operator: veh?.operator || '',
      });
    });
    return map;
  }, new Map()).values()].map(g => {
    // Saat mencari No Body: hanya tampilkan kendaraan yang cocok, bukan seluruh list teknisi
    let rows = g.rows;
    if (noBodyQuery) {
      rows = g.rows.filter(r => (r.body_no || '').toLowerCase().includes(noBodyQuery));
    }
    return {
      ...g,
      rows,
      periode: g.tglMulai && g.tglSelesai
        ? `${String(g.tglMulai).slice(0, 10)} s/d ${String(g.tglSelesai).slice(0, 10)}`
        : (g.tglMulai ? String(g.tglMulai).slice(0, 10) : '-'),
      keterangan: [...g.keterangan].join('; '),
    };
  });

  // Compute assigned vehicles from OTHER active penugasan (for duplicate prevention)
  const getAssignedVehicleMap = (excludePenugasanId) => {
    const map = {};
    data.forEach(p => {
      if (p.id === excludePenugasanId) return;
      if (p.status === 'Direncanakan' || p.status === 'Berlangsung') {
        p.vehicle_data?.forEach(v => {
          if (!map[v.id]) {
            map[v.id] = p.karyawan_nama || 'Teknisi lain';
          }
        });
      }
    });
    return map;
  };

  // Add assigned info to vehicles for display
  const getVehiclesWithAssignmentInfo = (excludePenugasanId) => {
    const assignedMap = getAssignedVehicleMap(excludePenugasanId);
    return vehicles.map(v => ({
      ...v,
      _assignedTo: assignedMap[v.id] || null,
    }));
  };

  const openCreate = () => {
    setEditing(null);
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    setForm({ ...emptyForm, tanggal_mulai: now.toISOString().slice(0, 10), tanggal_selesai: endOfMonth });
    setError('');
    setRolloverData(null);
    setCopyPreviousData(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      karyawan_id: item.karyawan_id,
      lokasi: item.lokasi || '', tanggal_mulai: item.tanggal_mulai?.slice(0, 10) || '',
      tanggal_selesai: item.tanggal_selesai?.slice(0, 10) || '', deskripsi: item.deskripsi || '',
      vehicle_data: item.vehicle_data || [],
    });
    setError('');
    setRolloverData(null);
    setShowModal(true);
  };

  // Load rollover & copy-previous data when teknisi is selected in create mode
  useEffect(() => {
    if (showModal && !editing && form.karyawan_id) {
      // Load rollover (incomplete vehicles only)
      getPenugasanRollover({ karyawan_id: form.karyawan_id })
        .then(res => {
          if (res.data?.total_incomplete > 0) {
            setRolloverData(res.data);
          } else {
            setRolloverData(null);
          }
        })
        .catch(() => setRolloverData(null));

      // Load copy-previous data (all vehicles from last penugasan)
      getPenugasanCopyPrevious({ karyawan_id: form.karyawan_id })
        .then(res => {
          if (res.data?.total_vehicles > 0) {
            setCopyPreviousData(res.data);
          } else {
            setCopyPreviousData(null);
          }
        })
        .catch(() => setCopyPreviousData(null));
    } else {
      setRolloverData(null);
      setCopyPreviousData(null);
    }
  }, [showModal, editing, form.karyawan_id]);

  // Add rollover vehicles to form
  const addRolloverVehicles = () => {
    if (!rolloverData?.vehicles) return;
    const existingIds = new Set(form.vehicle_data.map(v => v.id));
    const newVehicles = rolloverData.vehicles.filter(v => !existingIds.has(v.id));
    if (newVehicles.length === 0) return;

    setForm(f => ({
      ...f,
      vehicle_data: [...f.vehicle_data, ...newVehicles.map(v => ({
        id: v.id,
        no_polisi: v.no_polisi || '',
        no_body: v.no_body || '',
        brand: v.brand || '',
        trayek_no: v.trayek_no || '',
      }))],
    }));
    setRolloverData(null); // Hide banner after adding
  };

  // Copy all vehicles from previous penugasan
  const copyFromPrevious = () => {
    if (!copyPreviousData?.vehicles || copyPreviousData.vehicles.length === 0) return;
    setCopyLoading(true);
    try {
      const existingIds = new Set(form.vehicle_data.map(v => v.id));
      const newVehicles = copyPreviousData.vehicles.filter(v => !existingIds.has(v.id));
      if (newVehicles.length === 0) {
        setError('Semua kendaraan dari bulan lalu sudah ditambahkan');
        setCopyLoading(false);
        return;
      }

      setForm(f => ({
        ...f,
        vehicle_data: [...f.vehicle_data, ...newVehicles.map(v => ({
          id: v.id,
          no_polisi: v.no_polisi || '',
          no_body: v.no_body || '',
          brand: v.brand || '',
          trayek_no: v.trayek_no || '',
        }))],
      }));
      setError('');
    } finally {
      setCopyLoading(false);
    }
  };

  // Vehicle selection
  const addVehicle = (vehicleId) => {
    const v = vehicles.find(v => v.id === Number(vehicleId));
    if (!v) return;
    // Check if already added
    if (form.vehicle_data.some(vd => vd.id === v.id)) return;
    // Check if already assigned to another teknisi
    const assignedMap = getAssignedVehicleMap(editing?.id);
    if (assignedMap[v.id]) {
      setError(`Kendaraan ${v.body_no || ''} (${v.plate_no || ''}) sudah ditugaskan ke ${assignedMap[v.id]}`);
      return;
    }
    setForm(f => ({
      ...f,
      vehicle_data: [...f.vehicle_data, {
        id: v.id,
        no_polisi: v.plate_no || '',
        no_body: v.body_no || '',
        brand: v.brand || '',
        trayek_no: v.trayek_no || '',
      }],
    }));
  };

  const removeVehicle = (idx) => {
    setForm(f => ({ ...f, vehicle_data: f.vehicle_data.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    setError('');
    if (!form.karyawan_id) { setError('Karyawan harus dipilih'); return; }
    if (!form.tanggal_mulai) { setError('Tanggal mulai harus diisi'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        karyawan_id: Number(form.karyawan_id),
        tanggal_selesai: form.tanggal_selesai || null,
        vehicle_data: form.vehicle_data || [],
      };
      if (editing) {
        await updatePenugasan(editing.id, payload);
      } else {
        await createPenugasan(payload);
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Yakin ingin menghapus penugasan ini?`)) return;
    try { await deletePenugasan(item.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal menghapus'); }
  };

  // Get unique teknisi for filter
  const teknisiList = [...new Map(data.map(d => [d.karyawan_id, { id: d.karyawan_id, nama: d.karyawan_nama }])).values()].filter(t => t.id);

  // Build vehicles list for dropdown with assignment info
  const vehiclesForSearch = getVehiclesWithAssignmentInfo(editing?.id)
    .filter(v => !form.vehicle_data.some(vd => vd.id === v.id));

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Penugasan</h2>
            <p className="page-desc">{data.length} penugasan tercatat</p>
          </div>
          {hasMenuAction('penugasan', 'create') && (
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} /><span>Tambah Penugasan</span>
            </button>
          )}
        </div>
      </div>

      <div className="filters" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="search-box" style={{ minWidth: 180, flex: 1 }}>
          <Search size={18} />
          <input type="text" placeholder="Cari penugasan..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="search-box" style={{ minWidth: 180, flex: 1 }}>
          <Truck size={18} />
          <input type="text" placeholder="Cari No Body..." value={filterNoBody} onChange={e => setFilterNoBody(e.target.value)} />
        </div>
        <select value={filterKaryawan} onChange={e => setFilterKaryawan(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.82rem', background: '#fff', minWidth: 160 }}>
          <option value="">Semua Teknisi</option>
          {teknisiList.map(t => (
            <option key={t.id} value={t.id}>{t.nama}</option>
          ))}
        </select>
        {(filterKaryawan || filterNoBody) && (
          <button className="btn-icon" title="Reset filter" onClick={() => { setFilterKaryawan(''); setFilterNoBody(''); }} style={{ color: '#f5365c' }}>
            <X size={16} />
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: '0.75rem', color: 'var(--gray-500)', whiteSpace: 'nowrap', fontWeight: 500 }}>Tanggal:</label>
          <input type="date" value={filterTglAwal} onChange={e => setFilterTglAwal(e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.82rem', background: '#fff', opacity: filterNoBody.trim() ? 0.45 : 1 }}
            placeholder="Tgl Mulai" />
          <span style={{ color: 'var(--gray-300)' }}>—</span>
          <input type="date" value={filterTglAkhir} onChange={e => setFilterTglAkhir(e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.82rem', background: '#fff', opacity: filterNoBody.trim() ? 0.45 : 1 }}
            placeholder="Tgl Akhir" />
          {filterNoBody.trim() && (
            <span style={{ fontSize: '0.7rem', color: 'var(--warning, #f5a623)', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
              (diabaikan saat cari No Body)
            </span>
          )}
          {(filterTglAwal || filterTglAkhir) && (
            <button className="btn-icon" title="Reset tanggal" onClick={() => { setFilterTglAwal(''); setFilterTglAkhir(''); }} style={{ color: '#f5365c' }}>
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="page-loading">Memuat data...</div>
      ) : filtered.length === 0 ? (
        <div className="page-loading">Tidak ada data</div>
      ) : (
        <div className="penugasan-tables-grid">
          {groupedByTeknisi.map((group, gi) => (
            <PenugasanTable
              key={group.karyawan_id}
              items={group.items}
              rows={group.rows}
              teknisiName={group.karyawan_nama}
              periode={group.periode}
              keterangan={group.keterangan}
              color={TEKNISI_COLORS[gi % TEKNISI_COLORS.length]}
              canEdit={hasMenuAction('penugasan', 'edit')}
              canDelete={hasMenuAction('penugasan', 'delete')}
              onEdit={openEdit}
              onDelete={handleDelete}
              filterNoBody={filterNoBody}
            />
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Penugasan' : 'Tambah Penugasan'}</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}

              {/* Rollover suggestion banner */}
              {!editing && rolloverData && (
                <div style={{
                  background: '#fff8e1', border: '1px solid #ffecb3', borderRadius: 8,
                  padding: '12px 14px', marginBottom: 14,
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <ArrowRight size={16} style={{ color: '#f5a623', flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: '0.82rem', color: '#795548' }}>
                    <strong>{rolloverData.total_incomplete} kendaraan</strong> belum selesai dari penugasan sebelumnya ({rolloverData.previous_karyawan}).
                    Tambahkan ke penugasan ini?
                  </div>
                  <button className="btn btn-primary" style={{ padding: '5px 12px', fontSize: '0.78rem' }}
                    onClick={addRolloverVehicles}>
                    <Plus size={14} style={{ marginRight: 4 }} />Tambahkan
                  </button>
                </div>
              )}

              <h4 style={{ fontSize: '0.85rem', color: 'var(--gray-400)', textTransform: 'uppercase', marginBottom: 12 }}>Data Penugasan</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Teknisi *</label>
                  <select value={form.karyawan_id} onChange={e => setForm({...form, karyawan_id: e.target.value})}>
                    <option value="">-- Pilih Teknisi --</option>
                    {karyawanList.filter(k => {
                      if (k.status !== 'Aktif') return false;
                      const jabatan = (k.jabatan || '').toLowerCase();
                      const bagian = (k.bagian_name || '').toLowerCase();
                      return jabatan.includes('teknisi') || bagian.includes('teknisi');
                    }).map(k => (
                      <option key={k.id} value={k.id}>{k.nama} ({k.nik})</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 14 }}>
                  <div className="form-group" style={{ flex: 1, margin: 0 }}>
                    <label>Tanggal Mulai *</label>
                    <input type="date" value={form.tanggal_mulai} onChange={e => setForm({...form, tanggal_mulai: e.target.value})} />
                  </div>
                  <div className="form-group" style={{ flex: 1, margin: 0 }}>
                    <label>Tanggal Selesai</label>
                    <input type="date" value={form.tanggal_selesai} onChange={e => setForm({...form, tanggal_selesai: e.target.value})} />
                  </div>
                </div>
              </div>

              {/* Vehicle Assignment */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                margin: '16px 0 12px',
              }}>
                <h4 style={{ fontSize: '0.85rem', color: 'var(--gray-400)', textTransform: 'uppercase', margin: 0 }}>
                  <Truck size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Kendaraan untuk PM ({form.vehicle_data.length} dipilih)
                </h4>
                {!editing && copyPreviousData && copyPreviousData.total_vehicles > 0 && (
                  <button
                    className="btn btn-secondary"
                    onClick={copyFromPrevious}
                    disabled={copyLoading}
                    style={{ padding: '5px 12px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                    title={`Salin ${copyPreviousData.total_vehicles} kendaraan dari penugasan sebelumnya (${copyPreviousData.previous_date_range || ''})`}
                  >
                    {copyLoading ? 'Menyalin...' : (
                      <><ArrowRight size={12} style={{ marginRight: 4 }} />Salin dari Bulan Lalu</>
                    )}
                  </button>
                )}
              </div>
              {!editing && copyPreviousData && copyPreviousData.total_vehicles > 0 && form.vehicle_data.length === 0 && (
                <div style={{
                  background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 8,
                  padding: '10px 14px', marginBottom: 12,
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                }}>
                  <ArrowRight size={16} style={{ color: '#2e7d32', flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: '0.82rem', color: '#1b5e20' }}>
                    Ada <strong>{copyPreviousData.total_vehicles} kendaraan</strong> dari penugasan sebelumnya
                    {copyPreviousData.previous_karyawan ? ` (${copyPreviousData.previous_karyawan})` : ''}.
                    Klik tombol <strong>"Salin dari Bulan Lalu"</strong> untuk menyalin semua kendaraan.
                  </div>
                </div>
              )}

              {/* Selected vehicles */}
              <div style={{ background: '#fafafa', borderRadius: 8, border: '1px solid var(--gray-100)', padding: 10, marginBottom: 10 }}>
                {form.vehicle_data.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--gray-400)', fontSize: '0.82rem' }}>
                    Belum ada kendaraan dipilih. Cari dan tambahkan kendaraan di bawah.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                    {form.vehicle_data.map((v, vi) => (
                      <span key={vi} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '4px 8px', borderRadius: 6, fontSize: '0.78rem',
                        background: '#e8f4fd', color: '#0a66c2', border: '1px solid #d0e8fa',
                      }}>
                        <Truck size={12} />
                        <span>
                          <strong>{v.no_body || '-'}</strong>
                          {v.no_polisi ? <span style={{ color: 'var(--gray-500)' }}> ({v.no_polisi}{v.trayek_no ? `, ${v.trayek_no}` : ''})</span> : ''}
                        </span>
                        <X size={12} style={{ cursor: 'pointer', marginLeft: 2 }} onClick={() => removeVehicle(vi)} />
                      </span>
                    ))}
                  </div>
                )}

                {/* Searchable vehicle selector */}
                <VehicleSearchInput
                  vehicles={vehiclesForSearch}
                  onSelect={addVehicle}
                />
              </div>

              <div className="form-group" style={{ marginTop: 12 }}>
                <label>Deskripsi</label>
                <textarea rows={3} value={form.deskripsi} onChange={e => setForm({...form, deskripsi: e.target.value})} placeholder="Deskripsi penugasan, misalnya: PM rutin bulan Juli untuk kendaraan trayek..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
